import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  adminApi,
  authApi,
  getAdminTwoFactorLoginChallenge,
  getAdminSetupLoginReason,
  isAdminSetupRequiredLoginResponse,
  getLoginErrorMessage,
  getLoginErrorMessageFromResponse,
  getLoginErrorCodeFromResponse,
  getLoginErrorRedirectFromResponse,
  loginApi,
  clearAuthToken,
  persistAuthToken,
  restoreMemoryAccessTokenFromCookie,
  isHardUnauthenticatedFailure,
  getMemoryAccessToken,
  hasStoredAuthToken,
  hasStoredAuthSession,
  isUserRefreshUnavailable,
  tryAdminTokenRefresh,
  tryUserTokenRefresh,
  registerAuthReadyWaiter,
} from '@/lib/api';
import {
  ADMIN_ACCESS_CONTROL_PATH,
  ADMIN_DASHBOARD_PATH,
  ADMIN_PROFILE_PATH,
  ADMIN_SETUP_2FA_PATH,
} from '@/lib/admin-paths';
import {
  incrementCheckAuthCalls,
  incrementMeCalls,
  logAuthEvent,
  logCheckAuthFailed,
  logCheckAuthSuccess,
} from '@/lib/auth-debug';
import { shouldUseAdminAuth, shouldUseAdminLogin } from '@/lib/env';
import { clearWorkspaceSessionContext } from '@/lib/resolve-static-param';
import {
  traceAuthState,
  traceLogin,
  traceTokenSaved,
} from '@/lib/login-redirect-trace';
import { resolvePostLoginDestination } from '@/lib/role-dashboard';
import { getEffectiveRole, isAdminRole, isOrganizationStaffRole } from '@/lib/rbac';

export type HydrateAuthOptions = {
  reason?: string;
  force?: boolean;
  signal?: AbortSignal;
};

let authHydrationPromise: Promise<void> | null = null;
let lastHydrationAt = 0;
let lastHydrationToken: string | null = null;
const HYDRATION_COOLDOWN_MS = 2500;

function isTransientAuthError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const code = String((error as { code?: string })?.code ?? '');
  const message = String((error as { message?: string })?.message ?? '').toLowerCase();

  // Auth denial is never a transient outage.
  if (status === 401 || status === 403) return false;

  if (status === 500 || status === 502 || status === 503 || status === 504) return true;

  if (
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_NETWORK' ||
    code === 'ENOTFOUND' ||
    code === 'EAI_AGAIN'
  ) {
    return true;
  }

  if (message.includes('timeout') || message.includes('network error') || message.includes('failed to fetch')) {
    return true;
  }

  // Intentional cancels / session aborts are not "service slow".
  if (code === 'ERR_CANCELED' || message.includes('session expired') || message.includes('canceled')) {
    return false;
  }

  // No HTTP response at all → treat as network/DNS failure only.
  if (status == null && !(error as { response?: unknown })?.response) {
    return true;
  }

  return false;
}

async function fetchMeWithRetry(signal?: AbortSignal): Promise<Awaited<ReturnType<typeof adminApi.me>>> {
  incrementMeCalls();
  const useAdmin = shouldUseAdminAuth();
  const config = signal ? { signal } : undefined;
  try {
    return useAdmin ? await adminApi.me(config) : await authApi.me(config);
  } catch (error) {
    if (signal?.aborted) throw error;
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (useAdmin && status === 401) {
      incrementMeCalls();
      try {
        return await authApi.me(config);
      } catch {
        throw error;
      }
    }
    const code = (error as { code?: string })?.code;
    if (code === 'ECONNABORTED' || code === 'ERR_CANCELED') throw error;
    if (!isTransientAuthError(error)) throw error;
    logAuthEvent('checkAuth_retry', { endpoint: useAdmin ? '/admin/me' : '/auth/me' });
    await new Promise((resolve) => setTimeout(resolve, 600));
    if (signal?.aborted) throw error;
    incrementMeCalls();
    return useAdmin ? await adminApi.me(config) : await authApi.me(config);
  }
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;

  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof window !== 'undefined'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('utf8');

    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function isJwtExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload) return true;

  const exp = Number(payload.exp ?? 0);
  if (!Number.isFinite(exp) || exp <= 0) return true;
  return Date.now() >= exp * 1000;
}

function getJwtTwoFactorVerified(token: string | null): boolean | null {
  if (!token) return null;
  const payload = parseJwtPayload(token);
  if (!payload || !('two_factor_verified' in payload)) return null;
  return Boolean(payload.two_factor_verified);
}

export type UserTwoFactorState = {
  enabled?: boolean;
  required?: boolean;
  setup_required?: boolean;
  session_verified?: boolean;
};

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  user_type?: string;
  phone?: string;
  avatar?: string;
  roles: string[];
  permissions?: string[];
  organizer?: any;
  has_organization_access?: boolean;
  has_developer_access?: boolean;
  master_admin?: boolean;
  hierarchy_rank?: number;
  hierarchy_tier?: string;
  super_platform_access?: boolean;
  two_factor?: UserTwoFactorState;
  must_change_password?: boolean;
  email_verified?: boolean;
  email_verified_at?: string | null;
}

export function isAdminTwoFactorSetupRequired(user: User | null | undefined): boolean {
  return Boolean(user?.two_factor?.setup_required);
}

export function isAdminMustChangePassword(user: User | null | undefined): boolean {
  return Boolean(user?.must_change_password);
}

/** Prefer a just-issued OTP JWT over a stale /me session_verified=false race. */
function memoryJwtIsTwoFactorVerified(): boolean {
  if (typeof window === 'undefined') return false;
  return getJwtTwoFactorVerified(getMemoryAccessToken()) === true;
}

/** After OTP, /me can briefly report session_verified=false while the Bearer is verified. */
function reconcileUserTwoFactorFromJwt(user: User): User {
  if (!user.two_factor || !memoryJwtIsTwoFactorVerified()) return user;
  return {
    ...user,
    two_factor: {
      ...user.two_factor,
      session_verified: true,
      setup_required: false,
    },
  };
}

function shouldPreservePostOtpSession(reason: string, previousUser: User | null, wasAuthenticated: boolean): boolean {
  if (!previousUser || !wasAuthenticated) return false;
  if (reason === 'post_otp_background') return true;
  // AuthInitializer re-runs checkAuth({ reason: 'app_init' }) on login → dashboard
  // navigation. Do not wipe a just-verified OTP Bearer over a stale /me payload.
  if (reason === 'app_init' && memoryJwtIsTwoFactorVerified()) return true;
  return false;
}

/** 2FA is enabled but the current JWT was not issued after authenticator verification. */
export function isAdminTwoFactorSessionBlocked(user: User | null | undefined): boolean {
  if (!user) return false;

  if (isAdminTwoFactorSetupRequired(user)) {
    return false;
  }

  // Password-only / enforcement-disabled sessions are issued with
  // two_factor_verified=true even when 2FA is not enrolled yet.
  if (user.two_factor?.session_verified === true) return false;

  // Policy requires 2FA but it is not enrolled and setup was not offered —
  // force a fresh login rather than leaving a half-open admin session.
  if (user.two_factor?.required && !user.two_factor?.enabled) {
    return true;
  }

  if (!user.two_factor?.enabled) {
    return false;
  }

  // Stale /me can report session_verified=false while the OTP JWT is verified.
  if (user.two_factor.session_verified === false) {
    if (memoryJwtIsTwoFactorVerified()) return false;
    return true;
  }

  // Cookie-only sessions (post-reload) have no in-memory JWT. Trust the API's
  // session_verified flag above; do not treat a missing memory token as blocked.
  if (typeof window === 'undefined') return false;

  const token = getMemoryAccessToken();
  if (!token) return false;

  return getJwtTwoFactorVerified(token) !== true;
}

/** Block dashboard, badges, and other protected admin APIs until 2FA is fully satisfied. */
export function isAdminProtectedApiBlocked(user: User | null | undefined): boolean {
  if (!user) return true;
  // /admin/me has not hydrated two_factor yet — avoid 403 spam from admin modules.
  if (user.two_factor === undefined) return true;
  return isAdminTwoFactorSetupRequired(user) || isAdminTwoFactorSessionBlocked(user);
}

export function canFetchAdminProtectedApis(options: {
  user: User | null | undefined;
  authChecking: boolean;
  isAuthenticated: boolean;
}): boolean {
  const { user, authChecking, isAuthenticated } = options;
  if (authChecking || !isAuthenticated) return false;
  if (isAdminProtectedApiBlocked(user)) return false;
  // Wait for /admin/me to populate two_factor before calling protected admin APIs.
  if (user && user.two_factor === undefined) return false;
  return true;
}

export type AdminTwoFactorBlockCode = 'TWO_FACTOR_SETUP_REQUIRED' | 'TWO_FACTOR_REQUIRED';

export const ADMIN_TWO_FACTOR_CHALLENGE_SESSION_KEY = 'admin:2fa-login-challenge';
export {
  ADMIN_ACCESS_CONTROL_PATH,
  ADMIN_BASE_PATH,
  ADMIN_DASHBOARD_PATH,
  ADMIN_LOGIN_PATH,
  ADMIN_PROFILE_PATH,
  ADMIN_SETUP_2FA_PATH,
} from '@/lib/admin-paths';

export function resolveAdminBlockedPath(user: User | null | undefined): string {
  if (isAdminTwoFactorSetupRequired(user)) {
    return ADMIN_SETUP_2FA_PATH;
  }
  return ADMIN_ACCESS_CONTROL_PATH;
}

// Backend keeps the login challenge in cache for 5 minutes; expire the client
// copy slightly earlier so we never restore a token the backend already dropped
// (restoring a dead token made the first OTP submit fail with 401 CHALLENGE_EXPIRED).
const ADMIN_TWO_FACTOR_CHALLENGE_TTL_MS = (() => {
  const fromEnv = Number(process.env.NEXT_PUBLIC_ADMIN_TWO_FACTOR_CHALLENGE_TTL_MS);
  if (Number.isFinite(fromEnv) && fromEnv >= 60_000) return fromEnv;
  // Default 4.5 minutes — slightly under backend challenge TTL (default 5m).
  return 4.5 * 60 * 1000;
})();

export function readAdminTwoFactorLoginChallenge():
  | { challengeToken: string; email?: string }
  | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(ADMIN_TWO_FACTOR_CHALLENGE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      challengeToken?: string;
      email?: string;
      createdAt?: number;
    };
    const challengeToken =
      typeof parsed?.challengeToken === 'string' ? parsed.challengeToken.trim() : '';
    if (!challengeToken) return null;
    const createdAt = typeof parsed.createdAt === 'number' ? parsed.createdAt : 0;
    if (!createdAt || Date.now() - createdAt > ADMIN_TWO_FACTOR_CHALLENGE_TTL_MS) {
      sessionStorage.removeItem(ADMIN_TWO_FACTOR_CHALLENGE_SESSION_KEY);
      return null;
    }
    return {
      challengeToken,
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
    };
  } catch {
    return null;
  }
}

export function persistAdminTwoFactorLoginChallenge(
  challengeToken: string,
  email?: string | null
): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(
    ADMIN_TWO_FACTOR_CHALLENGE_SESSION_KEY,
    JSON.stringify({
      challengeToken,
      email: email ?? undefined,
      createdAt: Date.now(),
    })
  );
}

export function clearAdminTwoFactorLoginChallenge(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ADMIN_TWO_FACTOR_CHALLENGE_SESSION_KEY);
}

export const ADMIN_2FA_SETUP_REASON_KEY = 'admin:2fa-setup-reason';

export function persistAdminSetupReason(reason: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(ADMIN_2FA_SETUP_REASON_KEY, reason);
}

export function readAdminSetupReason(): string | null {
  if (typeof window === 'undefined') return null;
  const reason = sessionStorage.getItem(ADMIN_2FA_SETUP_REASON_KEY);
  return reason && reason.trim() ? reason.trim() : null;
}

export function clearAdminSetupReason(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(ADMIN_2FA_SETUP_REASON_KEY);
}

export function isAdminTwoFactorLoginRequiredError(
  error: unknown
): error is AdminTwoFactorLoginRequiredError {
  return (
    error instanceof AdminTwoFactorLoginRequiredError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as AdminTwoFactorLoginRequiredError).code === 'TWO_FACTOR_LOGIN_REQUIRED' &&
      typeof (error as AdminTwoFactorLoginRequiredError).challengeToken === 'string')
  );
}

export function isAdminTwoFactorChallengeExpiredError(
  error: unknown
): error is AdminTwoFactorChallengeExpiredError {
  return (
    error instanceof AdminTwoFactorChallengeExpiredError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as AdminTwoFactorChallengeExpiredError).code === 'TWO_FACTOR_CHALLENGE_EXPIRED')
  );
}

function getUserRole(raw: any): string {
  return getEffectiveRole(raw);
}

/** Normalize API user to frontend User (roles as string[], id as string) */
function normalizeUser(raw: any): User {
  const role = getUserRole(raw);
  const roles = Array.isArray(raw?.roles)
    ? (raw.roles as any[])
        .map((r) => (typeof r === 'string' ? r : r?.name ?? ''))
        .filter(Boolean)
        .map((r) => String(r).toLowerCase())
    : role
      ? [role]
      : [];
  const permissions = Array.isArray(raw?.permissions)
    ? raw.permissions.map((permission: unknown) => String(permission).toLowerCase()).filter(Boolean)
    : [];
  return {
    id: String(raw?.id ?? ''),
    name: raw?.name ?? '',
    email: raw?.email ?? '',
    role,
    user_type: role,
    phone: raw?.phone,
    avatar: raw?.avatar,
    roles,
    permissions,
    organizer: raw?.organizer,
    has_organization_access: Boolean(raw?.has_organization_access),
    has_developer_access: Boolean(raw?.has_developer_access),
    master_admin: Boolean(raw?.master_admin ?? raw?.is_master_admin),
    hierarchy_rank: typeof raw?.hierarchy_rank === 'number' ? raw.hierarchy_rank : Number(raw?.hierarchy_rank ?? 0),
    hierarchy_tier: raw?.hierarchy_tier ? String(raw.hierarchy_tier) : undefined,
    super_platform_access: Boolean(raw?.super_platform_access ?? raw?.master_admin ?? raw?.is_master_admin),
    two_factor: raw?.two_factor
      ? {
          enabled: Boolean(raw.two_factor.enabled),
          required: Boolean(raw.two_factor.required),
          setup_required: Boolean(raw.two_factor.setup_required),
          session_verified:
            raw.two_factor.session_verified === undefined
              ? undefined
              : Boolean(raw.two_factor.session_verified),
        }
      : undefined,
    must_change_password: Boolean(raw?.must_change_password),
    email_verified: typeof raw?.email_verified === 'boolean' ? raw.email_verified : undefined,
    email_verified_at: typeof raw?.email_verified_at === 'string' ? raw.email_verified_at : raw?.email_verified_at ?? undefined,
  };
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  authChecking: boolean;
  authError: string | null;
  _hasHydrated: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    options?: { redirectPath?: string; remember?: boolean; adminLogin?: boolean }
  ) => Promise<string>;
  register: (role: string, data: any) => Promise<string>;
  logout: () => Promise<void>;
  clearSessionLocal: () => void;
  setUser: (user: User | null) => void;
  applyAdminTwoFactorBlock: (code: AdminTwoFactorBlockCode) => void;
  getRedirectForUser: (user: User | null, options?: { redirectPath?: string }) => string;
  setHasHydrated: (value: boolean) => void;
  checkAuth: (options?: HydrateAuthOptions) => Promise<void>;
  refreshAdminAuth: () => Promise<User | null>;
  completeAdminTwoFactorLogin: (
    challengeToken: string,
    code: string,
    options?: { redirectPath?: string }
  ) => Promise<string>;
  becomeOrganizer: (data: any) => Promise<void>;
}

export class AdminTwoFactorLoginRequiredError extends Error {
  readonly code = 'TWO_FACTOR_LOGIN_REQUIRED';
  readonly challengeToken: string;
  readonly pendingUser: Record<string, unknown> | null;

  constructor(challengeToken: string, pendingUser: Record<string, unknown> | null) {
    super('Two-factor authentication is required to finish signing in.');
    this.name = 'AdminTwoFactorLoginRequiredError';
    this.challengeToken = challengeToken;
    this.pendingUser = pendingUser;
  }
}

export class AdminTwoFactorChallengeExpiredError extends Error {
  readonly code = 'TWO_FACTOR_CHALLENGE_EXPIRED';

  constructor(message = 'Your verification session expired. Sign in again to get a fresh code prompt.') {
    super(message);
    this.name = 'AdminTwoFactorChallengeExpiredError';
  }
}

export class AdminAccessDeniedError extends Error {
  readonly code = 'ADMIN_ACCESS_DENIED';

  constructor(message = 'This account does not have admin portal access.') {
    super(message);
    this.name = 'AdminAccessDeniedError';
  }
}

export class AdminLoginRequiredError extends Error {
  readonly code = 'ADMIN_LOGIN_REQUIRED';
  readonly redirect: string;

  constructor(
    message = 'Admin accounts must sign in through the admin portal.',
    redirect = '/login?admin=1'
  ) {
    super(message);
    this.name = 'AdminLoginRequiredError';
    this.redirect = redirect;
  }
}

export function isAdminAccessDeniedError(error: unknown): error is AdminAccessDeniedError {
  return (
    error instanceof AdminAccessDeniedError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as AdminAccessDeniedError).code === 'ADMIN_ACCESS_DENIED')
  );
}

export function isAdminLoginRequiredError(error: unknown): error is AdminLoginRequiredError {
  return (
    error instanceof AdminLoginRequiredError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as AdminLoginRequiredError).code === 'ADMIN_LOGIN_REQUIRED')
  );
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
    user: null,
    isLoading: false,
    authChecking: true,
    authError: null,
    _hasHydrated: false,
    isAuthenticated: false,

    setHasHydrated: (value: boolean) => set({ _hasHydrated: value }),

    getRedirectForUser: (user: User | null, options?: { redirectPath?: string }): string => {
      return resolvePostLoginDestination(user, { redirectPath: options?.redirectPath });
    },

    login: async (
      email: string,
      password: string,
      options?: { redirectPath?: string; remember?: boolean; adminLogin?: boolean }
    ): Promise<string> => {
      logAuthEvent('login_start');
      const emailVal = typeof email === 'string' ? email.trim() : '';
      traceLogin('LOGIN START', { email: emailVal });
      set({ isLoading: true, user: null, isAuthenticated: false, authError: null });
      try {
        const passwordVal = typeof password === 'string' ? password : '';

  if (!emailVal || !passwordVal) {
    throw new Error('Email and password are required.');
  }

        // Drop any stale bearer so concurrent checkAuth cannot "succeed" mid-login
        // and skip / interrupt the admin 2FA challenge UI.
        if (typeof window !== 'undefined') {
          clearAuthToken();
          localStorage.removeItem('auth-storage');
        }

        // Admin portal / ?admin=1 / pending OTP challenge → ONLY /admin/login.
        // Public marketing login → /auth/login, with one silent upgrade to
        // /admin/login when the backend returns ADMIN_LOGIN_REQUIRED.
        const pendingChallenge = typeof window !== 'undefined' ? readAdminTwoFactorLoginChallenge() : null;
        const useAdminLogin =
          options?.adminLogin === true ||
          shouldUseAdminLogin() ||
          Boolean(pendingChallenge?.challengeToken);
        let res = await loginApi.login(
          { email: emailVal, password: passwordVal },
          { adminLogin: useAdminLogin }
        );
        traceLogin('LOGIN RESPONSE', {
          status: res.status,
          success: res.data?.success,
          endpoint: useAdminLogin ? '/admin/login' : '/auth/login',
          hasToken: Boolean(
            res.data?.token ||
              res.data?.access_token ||
              res.data?.data?.access_token
          ),
          hasUser: Boolean(res.data?.user || res.data?.data?.user),
          twoFactorRequired: Boolean(
            res.data?.data?.two_factor_required ||
              res.data?.two_factor_required ||
              res.data?.data?.requires_2fa ||
              res.data?.requires_2fa
          ),
        });

        if (
          res.status >= 400 &&
          getLoginErrorCodeFromResponse(res.data) === 'ADMIN_LOGIN_REQUIRED' &&
          !useAdminLogin
        ) {
          const redirect =
            getLoginErrorRedirectFromResponse(res.data) ?? '/login?admin=1';
          if (typeof window !== 'undefined') {
            const next = new URL(window.location.href);
            next.searchParams.set('admin', '1');
            window.history.replaceState({}, '', `${next.pathname}${next.search}${next.hash}`);
          }
          traceLogin('ADMIN_LOGIN_REQUIRED → retry /admin/login', { redirect });
          res = await loginApi.login(
            { email: emailVal, password: passwordVal },
            { adminLogin: true }
          );
          traceLogin('ADMIN LOGIN RETRY RESPONSE', {
            status: res.status,
            success: res.data?.success,
            twoFactorRequired: Boolean(
              res.data?.data?.two_factor_required || res.data?.two_factor_required
            ),
          });
        }

        if (res.status >= 400) {
          if (getLoginErrorCodeFromResponse(res.data) === 'ADMIN_LOGIN_REQUIRED') {
            throw new AdminLoginRequiredError(
              undefined,
              getLoginErrorRedirectFromResponse(res.data) ?? '/login?admin=1'
            );
          }
          throw new Error(getLoginErrorMessageFromResponse(res.status, res.data));
        }

        // MUST run before any token persistence or dashboard redirect.
        // A 200 with two_factor_required is a successful challenge, not a failure.
        const twoFactorChallenge = getAdminTwoFactorLoginChallenge(res.data);
        if (twoFactorChallenge) {
          logAuthEvent('two_factor_required', {
            challengeTokenPresent: Boolean(twoFactorChallenge.challengeToken),
            challengeTokenLength: twoFactorChallenge.challengeToken.length,
          });
          const pendingEmail =
            typeof twoFactorChallenge.user?.email === 'string'
              ? twoFactorChallenge.user.email
              : emailVal;
          persistAdminTwoFactorLoginChallenge(
            twoFactorChallenge.challengeToken,
            pendingEmail
          );
          if (typeof window !== 'undefined') {
            const next = new URL(window.location.href);
            next.searchParams.set('admin', '1');
            next.searchParams.set('reason', 'two_factor');
            window.history.replaceState({}, '', `${next.pathname}${next.search}${next.hash}`);
          }
          set({ isLoading: false, user: null, isAuthenticated: false, authError: null });
          throw new AdminTwoFactorLoginRequiredError(
            twoFactorChallenge.challengeToken,
            twoFactorChallenge.user
          );
        }

        // Guard: backend signaled 2FA but payload was unreadable — never treat as logged-in.
        if (
          res.data?.data?.two_factor_required ||
          res.data?.two_factor_required ||
          res.data?.data?.requires_2fa ||
          res.data?.requires_2fa
        ) {
          set({ isLoading: false, user: null, isAuthenticated: false, authError: null });
          throw new Error(
            'Two-factor authentication is required, but the challenge token was missing. Please try signing in again.'
          );
        }

        const adminSetupRequired = isAdminSetupRequiredLoginResponse(res.data);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth-storage');
          clearAdminTwoFactorLoginChallenge();
          persistAuthToken(res.data, { remember: options?.remember });
          traceTokenSaved(
            adminSetupRequired ? 'auth-store.login.setup_required' : 'auth-store.login',
            res.data
          );
        }
        if (adminSetupRequired) {
          const setupReason = getAdminSetupLoginReason(res.data) ?? 'two_factor_setup_required';
          if (typeof window !== 'undefined') {
            persistAdminSetupReason(setupReason);
          }
          logAuthEvent('setup_required', { reason: setupReason });
          const rawSetupUser = res.data?.data?.user ?? res.data?.user ?? res.data?.data;
          const setupUser = normalizeUser(rawSetupUser);
          const setupRole = getUserRole(setupUser);
          if (!setupRole) {
            throw new Error('Missing role in user object');
          }
          set({ user: setupUser, isAuthenticated: true, isLoading: false });
          const destination = resolvePostLoginDestination(setupUser, {
            redirectPath: options?.redirectPath,
          });
          traceLogin('REDIRECT TARGET', { destination, role: setupRole, userId: setupUser.id, reason: 'setup_required' });
          logAuthEvent('login_success', { destination, setupRequired: true });
          return destination;
        }

        const rawUser = res.data?.user ?? res.data?.data?.user ?? res.data?.data;
        const user = normalizeUser(rawUser);
        const role = getUserRole(user);
        traceLogin('ROLE RESOLVED', { role, userId: user.id, email: user.email });
        if (!role) {
          console.error('Missing role in user object', user);
          throw new Error('Missing role in user object');
        }

        const adminPortalLogin = options?.adminLogin === true || shouldUseAdminLogin();
        if (adminPortalLogin && !isAdminRole(role)) {
          clearAuthToken();
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth-storage');
          }
          set({ user: null, isAuthenticated: false, isLoading: false });
          throw new AdminAccessDeniedError();
        }

        set({ user, isAuthenticated: true, isLoading: false, authError: null });
        traceAuthState('auth-store.login', {
          user,
          isAuthenticated: true,
          authChecking: false,
        });
        traceLogin('AUTH STATE UPDATED', {
          isAuthenticated: true,
          userId: user.id,
          role,
        });

        // Admin portal may need two_factor flags from /me; org staff already have access from login payload.
        if (adminPortalLogin && user.two_factor === undefined) {
          void useAuthStore.getState().checkAuth({ reason: 'post_login' });
        }

        const freshUser = user;
        const destination = resolvePostLoginDestination(freshUser, {
          redirectPath: options?.redirectPath,
        });
        traceLogin('REDIRECT TARGET', { destination, role, userId: user.id });
        logAuthEvent('login_success', { destination });
        return destination;
      } catch (error: any) {
        set({ isLoading: false });
        if (isAdminTwoFactorLoginRequiredError(error)) {
          throw error;
        }
        if (isAdminAccessDeniedError(error)) {
          throw error;
        }
        if (isAdminLoginRequiredError(error)) {
          throw error;
        }

        // If this is a plain Error thrown from inside the try block (e.g. res.status >= 400),
        // it already carries the correct user-facing message and has no Axios .response/.code.
        // Re-processing it through getLoginErrorMessage would discard the message and return
        // "Unable to reach the server" because plain Errors have no response property.
        // NOTE: AdminTwoFactorLoginRequiredError has a `.code` field — must be handled above.
        if (error instanceof Error && !(error as { response?: unknown }).response && !(error as { code?: unknown }).code) {
          throw error;
        }
        throw new Error(getLoginErrorMessage(error));
      }
    },

    register: async (role: string, data: any): Promise<string> => {
      set({ isLoading: true });
      try {
        const name = typeof data?.name === 'string' ? data.name.trim() : '';
        const email = typeof data?.email === 'string' ? data.email.trim().toLowerCase() : '';
        const password = typeof data?.password === 'string' ? data.password : '';

        if (!name || !email || !password) {
          throw new Error('Name, email, and password are required.');
        }

        const roleValue = String(role || 'participant').toLowerCase();
        const normalizedRole = roleValue === 'organization' ? 'organization_admin' : roleValue;
        const endpointPayload = { ...data, name, email, password };
        const res =
          ['participant', 'organizer', 'organization_admin', 'jury', 'team'].includes(normalizedRole)
            ? await authApi.registerByRole(normalizedRole as any, endpointPayload)
            : await authApi.register(endpointPayload);
        const rawUser = res.data?.user ?? res.data?.data?.user ?? res.data?.data;
        const user = rawUser ? normalizeUser(rawUser) : null;

        const redirectPath = String(res.data?.redirect ?? '/dashboard').trim() || '/dashboard';
        const redirectUrl = redirectPath.startsWith('http') ? redirectPath : redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;

        set({ user, isAuthenticated: Boolean(user), isLoading: false });

        return redirectUrl;
      } catch (error: any) {
        set({ isLoading: false });
        const data = error.response?.data;

        if (data?.errors && typeof data.errors === 'object') {
          const messages = Object.values(data.errors).flat();
          const msg = messages[0];
          throw typeof msg === 'string' ? msg : data?.message || 'Validation failed';
        }

        throw data?.message || error?.message || 'Registration failed';
      }
    },

    logout: async () => {
      // Clear local state IMMEDIATELY so the UI updates and persisted store
      // is wiped before any async work. This prevents the login page from
      // seeing a stale isAuthenticated=true and auto-redirecting back in.
      set({ user: null, isAuthenticated: false, authChecking: false, isLoading: false });
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth-storage');
        sessionStorage.removeItem('auth-storage');
        clearAuthToken();
        clearAdminTwoFactorLoginChallenge();
        clearWorkspaceSessionContext();
      }

      // Now hit the server logout endpoint to clear HttpOnly cookies.
      // This is fire-and-forget — local state is already wiped above.
      try {
        if (typeof window !== 'undefined') {
          const logoutRequest = shouldUseAdminAuth() ? adminApi.logout() : authApi.logout();
          await logoutRequest.catch(() => {
            // Ignore network/logout endpoint errors.
          });
          try {
            const { getFirebaseAuth } = await import('@/lib/firebase');
            const { signOut } = await import('firebase/auth');
            const firebaseAuth = getFirebaseAuth();
            if (firebaseAuth?.currentUser) {
              await signOut(firebaseAuth);
            }
          } catch {
            // Firebase may be disabled; local cleanup already done above.
          }
        }
      } catch {
        // All local state already cleared above — safe to swallow.
      }
    },

    clearSessionLocal: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth-storage');
        clearAuthToken();
        clearAdminTwoFactorLoginChallenge();
        clearWorkspaceSessionContext();
      }
      set({ user: null, isAuthenticated: false });
    },

    setUser: (user: User | null) => {
      set({ user, isAuthenticated: !!user });
    },

    applyAdminTwoFactorBlock: (code: AdminTwoFactorBlockCode) => {
      set((state) => {
        if (!state.user) return state;
        const twoFactor = { ...state.user.two_factor };
        if (code === 'TWO_FACTOR_SETUP_REQUIRED') {
          twoFactor.required = true;
          twoFactor.setup_required = true;
        } else {
          twoFactor.enabled = true;
          twoFactor.session_verified = false;
        }
        return {
          user: {
            ...state.user,
            two_factor: twoFactor,
          },
        };
      });
    },

    completeAdminTwoFactorLogin: async (
      challengeToken: string,
      code: string,
      options?: { redirectPath?: string }
    ): Promise<string> => {
      const token = typeof challengeToken === 'string' ? challengeToken.trim() : '';
      logAuthEvent('otp_start', {
        endpoint: '/admin/login/2fa',
        challengePresent: token.length >= 32,
      });
      // Backend requires challenge_token min:32. Never fire the request without
      // a plausible token — treat it as an expired challenge and restart login.
      // Uses ADMIN_LOGIN_2FA_ENDPOINT (/admin/login/2fa) — never /admin/security/2fa/verify
      // (that path is authenticator enrollment only and rejects login challenges).
      if (token.length < 32) {
        if (typeof window !== 'undefined') {
          clearAdminTwoFactorLoginChallenge();
        }
        throw new AdminTwoFactorChallengeExpiredError();
      }
      set({ isLoading: true, authError: null });
      try {
        const res = await adminApi.verifyTwoFactorLogin(token, code.trim());
        if (res.status >= 400) {
          const data = res.data;
          logAuthEvent('otp_rejected', {
            endpoint: '/admin/login/2fa',
            status: res.status,
            errorCode: typeof data?.error?.code === 'string' ? data.error.code : undefined,
            validationKeys: data?.error?.fields ? Object.keys(data.error.fields) : undefined,
          });
          const adminMessage =
            typeof data?.error?.message === 'string' ? data.error.message : null;
          if (data?.error?.code === 'VALIDATION_ERROR' && data?.error?.fields) {
            const fieldMessages = Object.values(data.error.fields as Record<string, string[]>)
              .flat()
              .filter((m): m is string => typeof m === 'string');
            throw fieldMessages[0] || adminMessage || 'Invalid 2FA payload.';
          }
          if (data?.error?.code === 'INVALID_OTP') {
            const remaining = Number(data?.error?.remaining_attempts);
            const suffix = Number.isFinite(remaining) && remaining > 0 ? ` ${remaining} attempt${remaining === 1 ? '' : 's'} left.` : '';
            throw `${adminMessage || data?.message || 'Invalid authenticator code.'}${suffix}`;
          }
          if (
            res.status === 401 ||
            data?.error?.code === 'UNAUTHORIZED' ||
            data?.error?.code === 'CHALLENGE_EXPIRED' ||
            data?.error?.code === 'TWO_FACTOR_RESET'
          ) {
            if (typeof window !== 'undefined') {
              clearAdminTwoFactorLoginChallenge();
            }
            throw new AdminTwoFactorChallengeExpiredError(adminMessage ?? undefined);
          }
          throw adminMessage || data?.message || 'Invalid verification code';
        }

        if (typeof window !== 'undefined') {
          clearAdminTwoFactorLoginChallenge();
          persistAuthToken(res.data);
        }

        const rawUser = res.data?.user ?? res.data?.data?.user ?? res.data?.data;
        const user = normalizeUser(rawUser);
        // OTP response may omit a true session_verified (request had no new Bearer).
        // Trust the JWT we just stored — otherwise protected admin APIs 403.
        if (user?.two_factor && typeof window !== 'undefined') {
          const jwtVerified = getJwtTwoFactorVerified(getMemoryAccessToken());
          if (jwtVerified === true) {
            user.two_factor = {
              ...user.two_factor,
              session_verified: true,
              setup_required: false,
            };
          }
        }
        const role = getUserRole(user);
        if (!role) {
          throw new Error('Missing role in user object');
        }

        const adminPortalLogin = shouldUseAdminLogin();
        if (adminPortalLogin && !isAdminRole(role)) {
          if (typeof window !== 'undefined') {
            clearAuthToken();
            localStorage.removeItem('auth-storage');
          }
          set({ user: null, isAuthenticated: false, isLoading: false });
          throw new AdminAccessDeniedError();
        }

        const storedToken = getMemoryAccessToken();
        lastHydrationAt = Date.now();
        lastHydrationToken = storedToken;
        set({ user, isAuthenticated: true, authChecking: false, authError: null, isLoading: false });

        // The 2FA response is already authoritative and sets the auth cookie/token.
        // Revalidating /admin/me synchronously can race that cookie write and bounce
        // the just-verified user back to /login. Let protected pages render, then
        // refresh in the background after the browser has committed credentials.
        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            void useAuthStore.getState().checkAuth({ reason: 'post_otp_background', force: true });
          }, 350);
        }

        const destination = resolvePostLoginDestination(user, {
          redirectPath: options?.redirectPath,
        });
        logAuthEvent('otp_success', { destination });
        return destination;
      } catch (error: any) {
        set({ isLoading: false });
        if (isAdminAccessDeniedError(error)) {
          throw error;
        }
        if (isAdminTwoFactorChallengeExpiredError(error)) {
          throw error;
        }
        const data = error?.response?.data ?? error;
        const adminMessage =
          typeof data?.error?.message === 'string' ? data.error.message : null;
        if (data?.error?.code === 'INVALID_OTP') {
          const remaining = Number(data?.error?.remaining_attempts);
          const suffix = Number.isFinite(remaining) && remaining > 0 ? ` ${remaining} attempt${remaining === 1 ? '' : 's'} left.` : '';
          throw `${adminMessage || data?.message || 'Invalid authenticator code.'}${suffix}`;
        }
        if (
          error?.response?.status === 401 ||
          data?.error?.code === 'UNAUTHORIZED' ||
          data?.error?.code === 'CHALLENGE_EXPIRED' ||
          data?.error?.code === 'TWO_FACTOR_RESET'
        ) {
          if (typeof window !== 'undefined') {
            clearAdminTwoFactorLoginChallenge();
          }
          throw new AdminTwoFactorChallengeExpiredError(adminMessage ?? undefined);
        }
        throw adminMessage || data?.message || error?.message || 'Verification failed';
      }
    },

    refreshAdminAuth: async () => {
      await useAuthStore.getState().checkAuth({ reason: 'refresh_admin' });
      return useAuthStore.getState().user;
    },

    checkAuth: async (options?: HydrateAuthOptions) => {
      const reason = options?.reason ?? 'unspecified';
      traceLogin('CHECKAUTH CALLED', { reason, force: options?.force ?? false });

      // Do not race the admin OTP step — a mid-challenge /me call can set
      // authError ("Auth service is slow…") and clear the 2FA UI.
      if (
        reason !== 'post_otp' &&
        typeof window !== 'undefined' &&
        readAdminTwoFactorLoginChallenge()
      ) {
        set({ authChecking: false, authError: null });
        return;
      }
      if (authHydrationPromise) {
        // force=true means the caller explicitly wants a fresh /me fetch
        // (e.g. after Google password setup). Reset the singleton guard.
        if (options?.force) {
          authHydrationPromise = null;
        } else {
          return authHydrationPromise;
        }
      }

      authHydrationPromise = (async () => {
        if (typeof window !== 'undefined') {
          restoreMemoryAccessTokenFromCookie();
        }
        let token = typeof window !== 'undefined' ? getMemoryAccessToken() : null;

        const hasSessionHint =
          typeof window !== 'undefined' &&
          Boolean(
            (token && !isJwtExpired(token)) ||
            hasStoredAuthToken() ||
            hasStoredAuthSession()
          );

        if (!token && !hasSessionHint) {
          set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
          return;
        }

        if ((!token || isJwtExpired(token)) && hasSessionHint && !isUserRefreshUnavailable()) {
          let refreshed = false;
          if (shouldUseAdminAuth()) {
            refreshed = await tryAdminTokenRefresh();
            if (!refreshed) {
              refreshed = await tryUserTokenRefresh();
            }
          } else {
            refreshed = await tryUserTokenRefresh();
          }
          token = getMemoryAccessToken();
          if (!refreshed || !token || isJwtExpired(token)) {
            // Do not wipe the session here. Cookie-backed /me may still succeed.
            traceLogin('CHECKAUTH REFRESH FAILED', {
              reason: 'refresh_failed_after_expired_token',
              hadToken: Boolean(token),
              willTryMe: true,
            });
          }
        }

        if (!token && !hasStoredAuthToken() && !hasStoredAuthSession()) {
          traceLogin('CHECKAUTH CLEARED SESSION', {
            reason: 'missing_or_expired_token',
            hadToken: false,
          });
          set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
          if (typeof window !== 'undefined') {
            localStorage.removeItem('auth-storage');
            clearAuthToken();
          }
          return;
        }

        if (
          !options?.force &&
          token === lastHydrationToken &&
          Date.now() - lastHydrationAt < HYDRATION_COOLDOWN_MS
        ) {
          logAuthEvent('checkAuth_skipped', { reason, cooldownMs: HYDRATION_COOLDOWN_MS });
          return;
        }

        incrementCheckAuthCalls(reason);
        const hasExistingUser = Boolean(useAuthStore.getState().user);
        if (hasExistingUser) {
          set({ authError: null });
        } else {
          set({ authChecking: true, authError: null });
        }

        const previousUser = useAuthStore.getState().user;
        const wasAuthenticated = useAuthStore.getState().isAuthenticated;

        try {
          const response = await fetchMeWithRetry(options?.signal);
          const rawUser =
            response.data?.data?.user ??
            response.data?.data ??
            response.data?.user;
          if (!rawUser) {
            // OptionalJwt /auth/me can return 200 + null for an expired bearer even after
            // we thought storage had a token. Attempt one refresh before giving up.
            if (typeof window !== 'undefined') {
              let refreshed = shouldUseAdminAuth()
                ? await tryAdminTokenRefresh()
                : await tryUserTokenRefresh();
              if (!refreshed && shouldUseAdminAuth()) {
                refreshed = await tryUserTokenRefresh();
              }
              if (refreshed || hasStoredAuthToken()) {
                const retry = await fetchMeWithRetry(options?.signal);
                const retryUser =
                  retry.data?.data?.user ??
                  retry.data?.data ??
                  retry.data?.user;
                if (retryUser) {
                    const user = reconcileUserTwoFactorFromJwt(normalizeUser(retryUser));
                    if (shouldUseAdminAuth() && isAdminTwoFactorSessionBlocked(user)) {
                      if (shouldPreservePostOtpSession(reason, previousUser, wasAuthenticated)) {
                        logCheckAuthFailed('post_otp_two_factor_blocked_grace', { reason });
                        set({
                          user: previousUser,
                          isAuthenticated: true,
                          authChecking: false,
                          authError: null,
                        });
                        return;
                      }
                      traceLogin('CHECKAUTH CLEARED SESSION', {
                        reason: 'two_factor_session_blocked',
                        checkAuthReason: reason,
                      });
                      set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
                      clearAuthToken();
                      localStorage.removeItem('auth-storage');
                      logCheckAuthFailed('two_factor_session_blocked', { reason });
                      return;
                    }
                    lastHydrationAt = Date.now();
                    lastHydrationToken = getMemoryAccessToken();
                    clearAdminTwoFactorLoginChallenge();
                    set({ user, isAuthenticated: true, authChecking: false, authError: null });
                    logCheckAuthSuccess(reason);
                    return;
                  }
              }
            }
            traceLogin('CHECKAUTH CLEARED SESSION', {
              reason: 'empty_user',
              meReturnedNull: true,
              checkAuthReason: reason,
            });
            if (shouldPreservePostOtpSession(reason, previousUser, wasAuthenticated)) {
              logCheckAuthFailed('post_otp_empty_user_grace', { reason });
              set({ user: previousUser, isAuthenticated: true, authChecking: false, authError: null });
              return;
            }
            set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
            if (typeof window !== 'undefined') {
              clearAuthToken();
              localStorage.removeItem('auth-storage');
            }
            logCheckAuthFailed('empty_user', { reason });
            return;
          }
          const user = reconcileUserTwoFactorFromJwt(normalizeUser(rawUser));
          if (shouldUseAdminAuth() && isAdminTwoFactorSessionBlocked(user)) {
            if (shouldPreservePostOtpSession(reason, previousUser, wasAuthenticated)) {
              logCheckAuthFailed('post_otp_two_factor_blocked_grace', { reason });
              set({
                user: previousUser,
                isAuthenticated: true,
                authChecking: false,
                authError: null,
              });
              return;
            }
            traceLogin('CHECKAUTH CLEARED SESSION', {
              reason: 'two_factor_session_blocked',
              checkAuthReason: reason,
            });
            set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
            if (typeof window !== 'undefined') {
              clearAuthToken();
              localStorage.removeItem('auth-storage');
            }
            logCheckAuthFailed('two_factor_session_blocked', { reason });
            return;
          }
          lastHydrationAt = Date.now();
          lastHydrationToken = token;
          if (typeof window !== 'undefined') {
            clearAdminTwoFactorLoginChallenge();
          }
          set({ user, isAuthenticated: true, authChecking: false, authError: null });
          traceLogin('CHECKAUTH SUCCESS', {
            reason,
            userId: user.id,
            role: user.role,
          });
          traceAuthState('checkAuth.success', useAuthStore.getState());
          logCheckAuthSuccess(reason);
        } catch (error: unknown) {
          if (
            options?.signal?.aborted ||
            (error as { name?: string })?.name === 'CanceledError' ||
            (error as { name?: string })?.name === 'AbortError' ||
            (error as { code?: string })?.code === 'ERR_CANCELED'
          ) {
            logCheckAuthFailed('aborted', { reason });
            set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
            if (typeof window !== 'undefined') {
              clearAuthToken();
              localStorage.removeItem('auth-storage');
            }
            throw error;
          }

          const status = (error as { response?: { status?: number; data?: { error?: { code?: string } } } })
            ?.response?.status;
          const errorCode = (error as { response?: { data?: { error?: { code?: string } } } })?.response?.data
            ?.error?.code;

          if (isTransientAuthError(error)) {
            logCheckAuthFailed('transient', { reason, status });
            set({
              user: previousUser,
              isAuthenticated: wasAuthenticated,
              authChecking: false,
              authError: 'Auth service is slow or unavailable. Please wait and try again.',
            });
            return;
          }

          if (!isHardUnauthenticatedFailure(status, errorCode)) {
            logCheckAuthFailed('non_auth_failure', { reason, status, errorCode });
            set({
              user: previousUser,
              isAuthenticated: wasAuthenticated,
              authChecking: false,
              authError: null,
            });
            return;
          }

          traceLogin('CHECKAUTH CLEARED SESSION', {
            reason: 'hard_failure',
            checkAuthReason: reason,
            status,
            errorCode,
          });
          if (shouldPreservePostOtpSession(reason, previousUser, wasAuthenticated)) {
            logCheckAuthFailed('post_otp_hard_failure_grace', { reason, status, errorCode });
            set({ user: previousUser, isAuthenticated: true, authChecking: false, authError: null });
            return;
          }
          logCheckAuthFailed('hard_failure', { reason, status, errorCode });
          set({ user: null, isAuthenticated: false, authChecking: false, authError: null });
          if (typeof window !== 'undefined') {
            clearAuthToken();
            localStorage.removeItem('auth-storage');
          }
        } finally {
          authHydrationPromise = null;
        }
      })();

      return authHydrationPromise;
    },

      becomeOrganizer: async (data: any) => {
        set({ isLoading: true });
        try {
          const res = await authApi.becomeOrganizer(data ?? {});
          if (res?.data && typeof res.data === 'object' && res.data.success === false) {
            const msg = typeof res.data.message === 'string' ? res.data.message : 'Unable to upgrade account';
            throw new Error(msg);
          }
          persistAuthToken(res?.data ?? {});
          await useAuthStore.getState().checkAuth();
        } catch (error) {
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Prevent reading localStorage during the first client render — that desyncs
      // SSR HTML (unhydrated) from the browser tree and triggers React #418/#423.
      skipHydration: true,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // If we already have a persisted user and a non-expired token, render
        // immediately and let checkAuth revalidate in the background. Only block
        // the UI when there is no usable persisted session.
        let hasValidSession = false;
        try {
          if (typeof window !== 'undefined') {
            restoreMemoryAccessTokenFromCookie();
          }
          const token =
            typeof window !== 'undefined' ? getMemoryAccessToken() : null;
          hasValidSession = Boolean(
            state.user && ((token && !isJwtExpired(token)) || hasStoredAuthToken()),
          );
        } catch {
          hasValidSession = false;
        }
        state.authChecking = !hasValidSession;
        state.setHasHydrated(true);
      },
    }
  )
);

export async function refreshAdminAuthState(): Promise<User | null> {
  return useAuthStore.getState().refreshAdminAuth();
}

/**
 * Returns a promise that resolves once auth hydration and any in-flight checkAuth completes.
 */
export function waitForAuthReady(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const state = useAuthStore.getState();
  if (state._hasHydrated && !state.authChecking) {
    return Promise.resolve();
  }
  if (authHydrationPromise) {
    return authHydrationPromise.then(() => {}).catch(() => {});
  }
  return new Promise<void>((resolve) => {
    let resolved = false;
    const unsub = useAuthStore.subscribe((curr) => {
      if (curr._hasHydrated && !curr.authChecking && !resolved) {
        resolved = true;
        unsub();
        resolve();
      }
    });
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unsub();
        resolve();
      }
    }, 4000);
  });
}

if (typeof window !== 'undefined') {
  registerAuthReadyWaiter(waitForAuthReady);
}

